import { IconButton, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import { ColoredIcon } from '../../ColoredIconAndChip';
import { arrowOverhang, arrowSize } from './arrowGeometry';

type Direction = 'left' | 'right';

type ArrowProps = { direction: Direction; label: string; onClick: () => void };

// Looks as the Carousel's arrow does: a round icon button on the paper colour with a lifted shadow,
// with the rounded chevron in the interactive fill colour. It sits as the Carousel lets a parent
// place it, offset: centred on the row's left or right edge and on the middle of the cards, so
// that it covers only the outer edge of the card at that end (see arrowGeometry).
function Arrow({ direction, label, onClick }: Readonly<ArrowProps>): ReactElement {
  const theme = useTheme();
  const iconName = direction === 'left' ? 'arrowLeftRounded' : 'arrowRightRounded';
  return (
    <IconButton
      aria-label={label}
      // Not a tab stop. A keyboard user reaches the cards with the arrow keys, and the row scrolls
      // the focused card into view. A tab stop here would also be lost when the button goes away
      // at the end of the row, while it has focus.
      tabIndex={-1}
      data-action={`scroll-${direction}`}
      onClick={onClick}
      sx={{
        position: 'absolute',
        // Level with the middle of the cards: the scrolling box's top padding and half a card.
        top: `calc(${theme.semantic.spacing['2xs']} + ${theme.identityCard.height} / 2)`,
        transform: 'translateY(-50%)',
        [direction]: `calc(-1 * ${arrowOverhang(theme)})`,
        // The smallest layer that puts the arrow over the card it overlaps. No theme token exists
        // for a local layer (the theme's zIndex steps start at 1100, for the app bar and above),
        // and the other components that need one write a raw 1 or 2 as well.
        zIndex: 1,
        width: arrowSize(theme),
        height: arrowSize(theme),
        padding: 0,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
      }}
    >
      <ColoredIcon
        iconName={iconName}
        isCompact
        sx={{ fill: theme.palette.interactive.enabled.fill }}
      />
    </IconButton>
  );
}

export type ScrollArrowsProps = {
  /** There is more to the left of what is in view, so the left arrow shows. */
  showLeft: boolean;
  /** There is more to the right of what is in view, so the right arrow shows. */
  showRight: boolean;
  leftLabel: string;
  rightLabel: string;
  onLeft: () => void;
  onRight: () => void;
};

/**
 * The pair of arrows that page a row of cards, as the Carousel's are. Each is in the page only
 * when there is more in its direction. Each is centred on the row's left or right edge, so it
 * covers only the outer edge of the card there and takes no room from the row: showing or hiding
 * one moves nothing. Place them beside the scrolling box, not inside it, in a positioned box that
 * is as wide as that box and does not clip: the box that scrolls would cut off the outer half.
 */
export function ScrollArrows({
  showLeft,
  showRight,
  leftLabel,
  rightLabel,
  onLeft,
  onRight,
}: Readonly<ScrollArrowsProps>): ReactElement {
  return (
    <>
      {showLeft && <Arrow direction="left" label={leftLabel} onClick={onLeft} />}
      {showRight && <Arrow direction="right" label={rightLabel} onClick={onRight} />}
    </>
  );
}
